import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Stack,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import QRCode from 'react-qr-code';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Smartphone } from 'lucide-react';
import { api, whatsappWEbApi } from '../../services/api';
import { AlertInfo } from '../../components/Alerts/Alert';

interface ConnectWhatsAppProps {
    onConnect: () => void;
}

const ConnectWhatsApp: React.FC<ConnectWhatsAppProps> = ({ onConnect }) => {
    const [stringInfoLogin] = useState(sessionStorage.getItem("infoLogin"));
    const [infoLogin] = useState(stringInfoLogin ? JSON.parse(stringInfoLogin) : null);
    const [isScanning, setIsScanning] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [check, setCheck] = useState(0);

    async function checkConnected() {
        setTimeout(async () => {
            const response = await whatsappWEbApi.get(`/whatsapp/status/spj${infoLogin?.id_escritorio_usuario}`);
            console.log(response);
            if (response && response.data) {
                if (response.data.status === "connected") {
                    const { phone_number, pushname } = response.data;
                    const data = {
                        session_id: `spj${infoLogin?.id_escritorio_usuario}`,
                        is_connected: 1,
                        phone_number,
                        name: pushname
                    };
                    await api.post(`/createinstance`, data);
                    return setIsScanning(false);
                }
            }
            setCheck(check + 1);
        }, 2000);
    }

    const handleStartScan = async () => {
        setDialogOpen(true);
        setIsScanning(true);
        try {
            const createInsance = await whatsappWEbApi.get(`/whatsapp/qrcode/spj${infoLogin?.id_escritorio_usuario}`);
            console.log(createInsance);
            if (createInsance.data && createInsance.data.status) {
                if (createInsance.data.qrcode) setQrCode(createInsance.data.qrcode);
                checkConnected();
            } else if (createInsance.data.error) {
                AlertInfo({ icon: 'error', title: createInsance.data.message });
            }
        } catch (error) {
            console.log(error);
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
    };

    const handleConnect = () => {
        setDialogOpen(false);
        onConnect();
    };

    useEffect(() => {
        if (check > 0) checkConnected();
    }, [check]);

    useEffect(() => {
        checkConnected();
    }, []);

    return (
        <>
            <Box sx={{ textAlign: 'center', py: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: '#25D366',
                            borderRadius: '50%',
                            p: 2,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Smartphone size={40} />
                    </Box>
                </Box>

                <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
                    Conecte a sua conta do WhatsApp
                </Typography>

                <Button
                    variant="contained"
                    size="large"
                    startIcon={<QrCode />}
                    onClick={handleStartScan}
                    sx={{
                        backgroundColor: '#25D366',
                        '&:hover': {
                            backgroundColor: '#128C7E',
                        },
                        px: 3,
                        py: 1.5
                    }}
                >
                    Conectar WhatsApp
                </Button>
            </Box>

            <Dialog
                open={dialogOpen}
                onClose={() => !isScanning && setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                    {isScanning ? 'Digitalize o código QR' : 'Conexão bem-sucedida'}
                </DialogTitle>

                <DialogContent>
                    {isScanning ? (
                        <Stack spacing={3} sx={{ py: 2, alignItems: 'center' }}>
                            <Box
                                sx={{
                                    width: 320,
                                    height: 320,
                                    backgroundColor: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                }}
                            >
                                {!qrCode && (
                                    <>
                                        <CircularProgress
                                            size={40}
                                            sx={{
                                                color: '#25D366',
                                                position: 'absolute'
                                            }}
                                        />
                                        <QrCode size={280} color="#333333" opacity={0.2} />
                                    </>
                                )}
                                {qrCode && (
                                    // <QRCode value={qrCode} size={310} />
                                    <QRCodeSVG value={qrCode} size={310} />
                                )}
                            </Box>

                            <Alert severity="info" sx={{ width: '100%' }}>
                                Abra o WhatsApp no ​​seu celular, toque em Menu ou Configurações e selecione WhatsApp Web. Aponte o celular para esta tela para capturar o código. Depois aguarde até a confirmação.
                            </Alert>
                        </Stack>
                    ) : (
                        <Stack spacing={2} sx={{ py: 2, alignItems: 'center' }}>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    backgroundColor: '#25D366',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1,
                                    color: 'white'
                                }}
                            >
                                <Smartphone size={40} />
                            </Box>

                            <Typography variant="h6" sx={{ textAlign: 'center' }}>
                                WhatsApp conectado com sucesso
                            </Typography>

                            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                                Sua conta do WhatsApp foi conectada à plataforma.
                            </Typography>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    {isScanning ? (
                        <Button
                            onClick={() => setDialogOpen(false)}
                            color="inherit"
                        >
                            Cancelar
                        </Button>
                    ) : (
                        <Button
                            onClick={handleConnect}
                            variant="contained"
                            sx={{
                                backgroundColor: '#25D366',
                                '&:hover': {
                                    backgroundColor: '#128C7E',
                                },
                            }}
                        >
                            Continuar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ConnectWhatsApp;